import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useTheme } from '@mui/material/styles'

const STORAGE_WIDTH = 'adams_desktop_nav_width_px'
const STORAGE_COLLAPSED = 'adams_desktop_nav_collapsed'

const COLLAPSED_WIDTH_PX = 72
const MIN_WIDTH_PX = 200
const MAX_WIDTH_PX = 420

const defaultContextValue = {
  collapsed: false,
  setCollapsed: () => {},
  widthPx: 256,
  setWidthPx: () => {},
  effectiveWidthPx: 256,
  toggleCollapsed: () => {},
  beginResize: () => {},
  COLLAPSED_WIDTH_PX,
  MIN_WIDTH_PX,
  MAX_WIDTH_PX,
}

const DesktopSidebarContext = createContext(defaultContextValue)

export function DesktopSidebarProvider({ children }) {
  const theme = useTheme()
  const standard = theme.mixins.drawerWidth ?? 256

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_COLLAPSED) === '1'
    } catch {
      return false
    }
  })

  const [widthPx, setWidthPx] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_WIDTH)
      const w = Number(raw)
      if (Number.isFinite(w) && w >= MIN_WIDTH_PX && w <= MAX_WIDTH_PX) {
        return w
      }
    } catch {
      /* ignore */
    }
    return standard
  })

  const effectiveWidthPx = collapsed ? COLLAPSED_WIDTH_PX : widthPx

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--adams-nav-drawer-width',
      `${effectiveWidthPx}px`,
    )
  }, [effectiveWidthPx])

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_COLLAPSED, collapsed ? '1' : '0')
    } catch {
      /* ignore */
    }
  }, [collapsed])

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_WIDTH, String(widthPx))
    } catch {
      /* ignore */
    }
  }, [widthPx])

  const toggleCollapsed = useCallback(() => {
    setCollapsed((c) => !c)
  }, [])

  const beginResize = useCallback(
    (event) => {
      if (collapsed) return
      event.preventDefault()
      const startX = event.clientX
      const startW = widthPx
      const dir = theme.direction === 'rtl' ? -1 : 1

      const onMove = (ev) => {
        const delta = (ev.clientX - startX) * dir
        const next = Math.min(
          MAX_WIDTH_PX,
          Math.max(MIN_WIDTH_PX, startW + delta),
        )
        setWidthPx(next)
      }

      const onUp = () => {
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
      }

      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
    },
    [collapsed, theme.direction, widthPx],
  )

  const value = useMemo(
    () => ({
      collapsed,
      setCollapsed,
      widthPx,
      setWidthPx,
      effectiveWidthPx,
      toggleCollapsed,
      beginResize,
      COLLAPSED_WIDTH_PX,
      MIN_WIDTH_PX,
      MAX_WIDTH_PX,
    }),
    [
      collapsed,
      widthPx,
      effectiveWidthPx,
      toggleCollapsed,
      beginResize,
    ],
  )

  return (
    <DesktopSidebarContext.Provider value={value}>
      {children}
    </DesktopSidebarContext.Provider>
  )
}

export function useDesktopSidebar() {
  return useContext(DesktopSidebarContext)
}
